import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';

/**
 * FilterChips Component
 *
 * Displays active filters as removable chips
 *
 * @param {Object} props
 * @param {Array} props.chips - Array of chip objects with { key, value, onRemove }
 * @param {string} [props.className] - Additional CSS classes
 *
 * @example
 * <FilterChips
 *   chips={[
 *     { key: 'date', value: 'Last 30 days', onRemove: () => resetFilter('date') },
 *     { key: 'status', value: 'Pending', onRemove: () => resetFilter('status') }
 *   ]}
 * />
 */
export function FilterChips({ chips = [], className = '' }) {
  if (!chips || chips.length === 0) {
    return null;
  }

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {chips.map((chip) => (
        <div
          key={chip.key}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-brand/5 text-xs rounded-full text-brand"
        >
          <span>{chip.value}</span>
          <button
            onClick={chip.onRemove}
            className="hover:text-brand-dark transition-colors cursor-pointer"
            aria-label={`Remove ${chip.value} filter`}
          >
            <Icon icon="lucide:x" className="size-3" />
          </button>
        </div>
      ))}
    </div>
  );
}

FilterChips.propTypes = {
  chips: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      onRemove: PropTypes.func.isRequired,
    })
  ),
  className: PropTypes.string,
};
